import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ProductService } from '../../core/product/product.service';
import { EventTrackingService
       } from '../../core/tracking/event-tracking.service';
import { Utils } from '../../core/utils';
import { StageUtils } from '../../core/utils.stage';
import { AbstractButtonConfig, ButtonsAssignment
       } from '../dialog-buttons.component';

import { DialogComponent } from '../dialog.component';

export enum UnchangedPlaceholdersChoice
{
  FIX,
  CLICK_TEXT,
  CLICK_IMAGE,
  IGNORE,
  CLICK_TITLE,
  CLEAR,
  IGNORE_IMAGES_CLEAR_TITLE,
}

export interface UnchangedPlaceholdersContext
{
  componentType: string | undefined;
  placeholders: Array<StageUtils.PlaceholderContext>;
  changeableElementsCount: number;
}

type UnchangedPlaceholdersButtonConfig =
    AbstractButtonConfig<UnchangedPlaceholdersChoice>;

enum DialogVersion {
  TEXT,
  IMAGE,
  MISC,
  BOX_TITLE
}

interface UIValues {
  titleKey: string;
  messageKey: string;
  iconUrl: string;
  buttons: ButtonsAssignment;
}

class UnchangedPlaceholdersButton
{
  public static FIX: UnchangedPlaceholdersButtonConfig = {
    labelKey: 'i18n.button.cancelAndFix',
    choice: UnchangedPlaceholdersChoice.FIX,
  };

  public static CLICK_TEXT: UnchangedPlaceholdersButtonConfig = {
    labelKey: 'i18n.button.cancelAndFix',
    choice: UnchangedPlaceholdersChoice.CLICK_TEXT,
  };

  public static CLICK_IMAGE: UnchangedPlaceholdersButtonConfig = {
    labelKey: 'i18n.button.cancelAndAddImage',
    choice: UnchangedPlaceholdersChoice.CLICK_IMAGE,
  };

  public static IGNORE: UnchangedPlaceholdersButtonConfig = {
    labelKey: 'i18n.button.ignoreAndContinueOrder',
    choice: UnchangedPlaceholdersChoice.IGNORE,
  };

  public static CLICK_TITLE: UnchangedPlaceholdersButtonConfig = {
    labelKey: 'i18n.dialog.unchangedPlaceholders.buttons.addTitle.label',
    choice: UnchangedPlaceholdersChoice.CLICK_TITLE,
  };

  public static CLEAR: UnchangedPlaceholdersButtonConfig = {
    labelKey: 'i18n.dialog.unchangedPlaceholders.buttons.clear.label',
    choice: UnchangedPlaceholdersChoice.CLEAR,
  };

  public static IGNORE_IMAGES_CLEAR_TITLE: UnchangedPlaceholdersButtonConfig = {
    labelKey: 'i18n.button.ignoreAndContinueOrder',
    choice: UnchangedPlaceholdersChoice.IGNORE_IMAGES_CLEAR_TITLE,
  };
}

@Component({
    selector: 'unchanged-placeholders-dialog',
    templateUrl: './unchanged-placeholders-dialog.component.html',
    styleUrls: ['../dialog.scss'],
    standalone: false
})
export class UnchangedPlaceholdersDialogComponent extends DialogComponent
{
  public unchangedPlaceholdersCount: number;
  public uiValues: UIValues;
  public textContent: string | undefined = undefined;

  constructor(@Inject(MAT_DIALOG_DATA) protected dialogData: any,
              @Inject(EventTrackingService) private eventTrackingService,
              @Inject(ProductService) private productService)
  {
    super(dialogData);

    this.uiValues = this.determineUIValues(dialogData);
    this.unchangedPlaceholdersCount = this.dialogData.placeholders.length;

    if (this.dialogData.placeholders[0].kind ===
        StageUtils.PlaceholderKind.TEXT) {
      this.textContent = this.dialogData.placeholders[0].node.textContent;
    }
  }

  private determineUIValues(context: UnchangedPlaceholdersContext): UIValues
  {
    switch (this.determineDialogVersion(context)) {
      case DialogVersion.TEXT: return {
        titleKey: 'i18n.dialog.unchangedPlaceholders.text.title',
        messageKey: 'i18n.dialog.unchangedPlaceholders.text.message',
        iconUrl: Utils.url('./assets/img/msg-unchanged-text.svg'),
        buttons: {
          primary: UnchangedPlaceholdersButton.IGNORE,
          secondary: UnchangedPlaceholdersButton.CLICK_TEXT,
        },
      };
      case DialogVersion.IMAGE: return {
        titleKey: 'i18n.dialog.unchangedPlaceholders.image.title',
        messageKey: 'i18n.dialog.unchangedPlaceholders.image.message',
        iconUrl: Utils.url('./assets/img/msg-unchanged-image.svg'),
        buttons: {
          primary: UnchangedPlaceholdersButton.CLICK_IMAGE,
          secondary: (
            (context.changeableElementsCount - context.placeholders.length > 0)
                ? UnchangedPlaceholdersButton.IGNORE
                : undefined),
        },
      };
      case DialogVersion.MISC: return {
        titleKey: 'i18n.dialog.unchangedPlaceholders.text.title',
        messageKey: 'i18n.dialog.unchangedPlaceholders.text.message',
        iconUrl: Utils.url('./assets/img/msg-warning.svg'),
        buttons: {
          primary: UnchangedPlaceholdersButton.FIX,
          secondary: this.allowEmptyMiscPlaceholders(context) ?
              UnchangedPlaceholdersButton.IGNORE_IMAGES_CLEAR_TITLE :
              undefined,
        },
      };
      case DialogVersion.BOX_TITLE: return {
        titleKey: 'i18n.dialog.unchangedPlaceholders.boxTitle.title',
        messageKey: 'i18n.dialog.unchangedPlaceholders.boxTitle.message',
        iconUrl: Utils.url('./assets/img/msg-unchanged-boxTitle.svg'),
        buttons: {
          primary: UnchangedPlaceholdersButton.CLEAR,
          secondary: UnchangedPlaceholdersButton.CLICK_TITLE,
        },
      };
      default: return <any>undefined; // cannot be reached
    }
  }

  private determineDialogVersion(
    ctx: UnchangedPlaceholdersContext): DialogVersion
  {
    const isOneKind = (kind: StageUtils.PlaceholderKind) =>
      ctx.placeholders.every(p => p.kind === kind);

    if (isOneKind(StageUtils.PlaceholderKind.IMAGE)) {
      return DialogVersion.IMAGE;
    }
    else if (isOneKind(StageUtils.PlaceholderKind.TEXT)) {
      if (ctx.placeholders.length === 1 && StageUtils.isBoxTitle(
          ctx.componentType, ctx.placeholders[0].id))
      {
        this.eventTrackingService.track({
          category: this.productService.getCurrentProduct().id,
          action: 'display_error_message_text',
          label: undefined,
          value: undefined,
        });
        return DialogVersion.BOX_TITLE;
      } else {
        return DialogVersion.TEXT;
      }
    }
    else {
      return DialogVersion.MISC;
    }
  }

  private allowEmptyMiscPlaceholders(
      ctx: UnchangedPlaceholdersContext): boolean
  {
    // if any placeholder was changed
    if (ctx.changeableElementsCount - ctx.placeholders.length > 0) {
      return true;
    }

    // if any text placeholder is not user defined
    const textPlaceholders = ctx.placeholders.filter(
        p => p.kind === StageUtils.PlaceholderKind.TEXT);
    if (textPlaceholders.some(p => !StageUtils.isUserDefined(p))) {
      return true;
    }

    return false;
  }
}
